import { Fragment, useEffect, useState } from "react";
import { RotateCcw, ShieldCheck, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const agrupar = (permisos) => {
  const grupos = [];
  permisos.forEach((p) => {
    const g = grupos.find((x) => x.titulo === p.grupo);
    if (g) g.items.push(p);
    else grupos.push({ titulo: p.grupo, items: [p] });
  });
  return grupos;
};

export const MatrizPermisos = () => {
  const [datos, setDatos] = useState(null);
  const [matriz, setMatriz] = useState({});
  const [guardando, setGuardando] = useState(null);

  const cargar = () => {
    api.get("/roles/permisos")
      .then((r) => { setDatos(r.data); setMatriz(r.data.matriz); })
      .catch(() => setDatos({ error: true }));
  };

  useEffect(cargar, []);

  if (!datos) return <Loading label="Cargando la matriz de permisos…" />;
  if (datos.error) {
    return <Empty testId="matriz-sin-acceso">Su rol no puede editar la matriz de permisos.</Empty>;
  }

  const editables = datos.roles.filter((r) => r.editable);
  const cambiado = (rol) =>
    JSON.stringify([...(matriz[rol] || [])].sort()) !== JSON.stringify([...(datos.matriz[rol] || [])].sort());

  const alternar = (rol, clave, valor) => {
    const actual = new Set(matriz[rol] || []);
    if (valor) actual.add(clave); else actual.delete(clave);
    setMatriz({ ...matriz, [rol]: [...actual].sort() });
  };

  const guardar = async (rol) => {
    setGuardando(rol);
    try {
      await api.put(`/roles/permisos/${rol}`, { permisos: matriz[rol] || [] });
      toast.success(`Permisos de ${rol} actualizados: rigen de inmediato`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
    setGuardando(null);
  };

  const restablecer = async (rol) => {
    try {
      const { data } = await api.post(`/roles/permisos/${rol}/restablecer`);
      toast.success(`Permisos de ${rol} restablecidos a los del sistema`);
      setMatriz((prev) => ({ ...prev, [rol]: data.permisos }));
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="matriz-permisos">
      <div className="mb-6 rounded-lg border border-border bg-muted/30 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <h2 className="font-display text-base font-semibold">Matriz de control de acceso</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Marque lo que puede hacer cada rol. El cambio rige de inmediato: el servidor consulta
              esta matriz en cada petición y el menú de cada portal se arma con estos permisos.
              La administración conserva siempre todos los permisos.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {datos.roles.map((r) => (
            <Badge key={r.rol} variant={r.editable ? "outline" : "secondary"}
                   data-testid={`rol-chip-${r.rol}`}>
              {r.etiqueta} · {r.usuarios} usuario(s)
            </Badge>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="tabla-matriz">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Permiso</th>
                {editables.map((r) => (
                  <th key={r.rol} className="px-4 py-3 text-center">{r.etiqueta}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agrupar(datos.permisos).map((g) => (
                <Fragment key={g.titulo}>
                  <tr className="border-t border-border/60 bg-muted/20">
                    <td colSpan={editables.length + 1}
                        className="px-6 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                      {g.titulo}
                    </td>
                  </tr>
                  {g.items.map((p) => (
                    <tr key={p.clave} className="border-t border-border/60 hover:bg-muted/20">
                      <td className="px-6 py-3">
                        <p>{p.etiqueta}</p>
                        <p className="font-mono text-[10px] text-muted-foreground">{p.clave}</p>
                      </td>
                      {editables.map((r) => (
                        <td key={r.rol} className="px-4 py-3 text-center">
                          <Checkbox
                            checked={(matriz[r.rol] || []).includes(p.clave)}
                            data-testid={`permiso-${r.rol}-${p.clave}`}
                            onCheckedChange={(v) => alternar(r.rol, p.clave, !!v)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {editables.map((r) => (
          <div key={r.rol} className="flex items-center gap-2 rounded-full border border-border px-3 py-2">
            <span className="text-sm font-semibold">{r.etiqueta}</span>
            <Button size="sm" className="rounded-full" disabled={!cambiado(r.rol) || guardando === r.rol}
                    onClick={() => guardar(r.rol)} data-testid={`guardar-permisos-${r.rol}`}>
              <Save className="mr-1 h-3 w-3" />
              {guardando === r.rol ? "Guardando…" : "Guardar"}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full" title="Volver a los permisos del sistema"
                    onClick={() => restablecer(r.rol)} data-testid={`restablecer-permisos-${r.rol}`}>
              <RotateCcw className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
